import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DollarSign, Users, Shield, ShieldCheck, Trash2, UserCheck, UserMinus, Loader2, Search, AlertTriangle, Star, Settings2, ChevronDown, ChevronUp, Ban, Play, Crown, Gift, Mail, CalendarX, History, RotateCcw, Flag, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AdminPasswordDialog from "@/components/AdminPasswordDialog";
import AdminDashboardStats from "@/components/AdminDashboardStats";

export default function AdminPanelPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState({ subscription_price: 10, platform_fee_rate: 0.03, min_price_per_night: 5 });
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Bulk-delete state: a Set of user ids checked in the table
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkDialog, setBulkDialog] = useState(null); // { password, error }
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [filterRole, setFilterRole] = useState("all"); // all | admin | user | suspended
  const [sortBy, setSortBy] = useState("recent"); // recent | oldest | name | spaces | bookings
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [promotePassword, setPromotePassword] = useState("");
  const [promoting, setPromoting] = useState(false);
  // Generic admin password dialog state for config-save + delete
  const [pwdDialog, setPwdDialog] = useState(null); // { kind: 'config' | 'delete', target?, error? }
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  // Gift subscription dialog
  const [giftTarget, setGiftTarget] = useState(null);
  const [giftDuration, setGiftDuration] = useState("3m");
  const [giftSubmitting, setGiftSubmitting] = useState(false);
  // Email edit dialog
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");
  // Revoke subscription dialog
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);
  // History dialog
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Refunds list
  const [refundsOpen, setRefundsOpen] = useState(false);
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [refundsData, setRefundsData] = useState({ rows: [], total_refunded: 0, total_fees_kept: 0 });
  // Reports / signalements
  const [reportsOpen, setReportsOpen] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportsFilter, setReportsFilter] = useState("pending");

  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") { navigate("/"); return; }
    api.get("/admin/config").then(r => setConfig({
      subscription_price: r.data.subscription_price,
      platform_fee_rate: r.data.platform_fee_rate,
      min_price_per_night: r.data.min_price_per_night ?? 5,
    })).catch(() => {});
    loadUsers("");
  }, [user, navigate]);

  const loadUsers = async (q) => {
    setLoadingUsers(true);
    try {
      const { data } = await api.get("/admin/users", { params: { search: q } });
      setUsers(data);
    } catch { toast.error("Erreur chargement utilisateurs"); }
    finally { setLoadingUsers(false); }
  };

  const saveConfig = () => {
    setPwdDialog({ kind: "config", error: "" });
  };

  const submitConfigWithPassword = async (password) => {
    setPwdSubmitting(true);
    try {
      const { data } = await api.put("/admin/config", {
        subscription_price: parseFloat(config.subscription_price),
        platform_fee_rate: parseFloat(config.platform_fee_rate),
        min_price_per_night: parseFloat(config.min_price_per_night),
        admin_password: password,
      });
      setConfig({
        subscription_price: data.subscription_price,
        platform_fee_rate: data.platform_fee_rate,
        min_price_per_night: data.min_price_per_night ?? 5,
      });
      toast.success("Tarifs mis à jour");
      setPwdDialog(null);
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur";
      setPwdDialog((d) => d ? { ...d, error: msg } : d);
    } finally { setPwdSubmitting(false); }
  };

  const toggleSuspend = async (u) => {
    const next = !u.suspended;
    try {
      await api.put(`/admin/users/${u.id}/suspend`, { suspended: next });
      toast.success(next ? `${u.email} suspendu` : `${u.email} reactive`);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, suspended: next } : x));
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  // Bulk-delete helpers
  const toggleBulk = (uid) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };
  const isAllDeletableSelected = (() => {
    const deletable = users.filter(u => u.id !== user?.id && !u.is_super_admin);
    if (deletable.length === 0) return false;
    return deletable.every(u => bulkSelected.has(u.id));
  })();
  const toggleBulkAll = () => {
    setBulkSelected(prev => {
      if (isAllDeletableSelected) return new Set();
      const next = new Set(prev);
      users.forEach(u => { if (u.id !== user?.id && !u.is_super_admin) next.add(u.id); });
      return next;
    });
  };
  const submitBulkDelete = async (password) => {
    if (!password) { setBulkDialog(d => ({ ...d, error: "Mot de passe requis" })); return; }
    setBulkSubmitting(true);
    try {
      const ids = Array.from(bulkSelected);
      const { data } = await api.post("/admin/users/bulk-delete", { user_ids: ids, admin_password: password });
      const deletedCount = data.deleted_count || 0;
      const skippedCount = (data.skipped || []).length;
      toast.success(`${deletedCount} compte${deletedCount > 1 ? "s" : ""} supprimé${deletedCount > 1 ? "s" : ""}${skippedCount > 0 ? ` (${skippedCount} ignoré${skippedCount > 1 ? "s" : ""})` : ""}`);
      setBulkSelected(new Set());
      setBulkDialog(null);
      loadUsers(search);
    } catch (err) {
      const detail = err.response?.data?.detail || "Erreur";
      if (err.response?.status === 403 || /mot de passe/i.test(detail)) {
        setBulkDialog(d => ({ ...d, error: detail }));
      } else {
        toast.error(detail);
        setBulkDialog(null);
      }
    } finally { setBulkSubmitting(false); }
  };

  const filteredSortedUsers = (() => {
    let arr = [...users];
    if (filterRole === "admin") arr = arr.filter(u => u.role === "admin");
    else if (filterRole === "user") arr = arr.filter(u => u.role !== "admin" && !u.suspended);
    else if (filterRole === "suspended") arr = arr.filter(u => u.suspended);

    const cmpName = (a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || "", "fr", { sensitivity: "base" });
    const tm = (u) => new Date(u.created_at || 0).getTime();
    switch (sortBy) {
      case "oldest": arr.sort((a, b) => tm(a) - tm(b)); break;
      case "name": arr.sort(cmpName); break;
      case "spaces": arr.sort((a, b) => (b.spaces_count || 0) - (a.spaces_count || 0)); break;
      case "bookings": arr.sort((a, b) => (b.bookings_count || 0) - (a.bookings_count || 0)); break;
      case "recent":
      default: arr.sort((a, b) => tm(b) - tm(a));
    }
    return arr;
  })();

  const toggleAdmin = async (u) => {
    // Demotion: no password required
    if (u.role === "admin") {
      try {
        await api.put(`/admin/users/${u.id}/role`, { role: "user" });
        toast.success(`Droits admin revoques pour ${u.email}`);
        setUsers(prev => prev.map(x => x.id === u.id ? { ...x, role: "user" } : x));
      } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
      return;
    }
    // Promotion: open password confirmation dialog
    setPromoteTarget(u);
    setPromotePassword("");
  };

  const confirmPromote = async () => {
    if (!promoteTarget || !promotePassword) { toast.error("Saisissez votre mot de passe"); return; }
    setPromoting(true);
    try {
      await api.put(`/admin/users/${promoteTarget.id}/role`, { role: "admin", password: promotePassword });
      toast.success(`${promoteTarget.email} est maintenant admin`);
      setUsers(prev => prev.map(x => x.id === promoteTarget.id ? { ...x, role: "admin" } : x));
      setPromoteTarget(null);
      setPromotePassword("");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setPromoting(false); }
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setPwdDialog({ kind: "delete", target: deleteTarget, error: "" });
  };

  const submitDeleteWithPassword = async (password) => {
    if (!deleteTarget) return;
    setPwdSubmitting(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`, { data: { admin_password: password } });
      toast.success(`Compte ${deleteTarget.email} supprime`);
      setUsers(prev => prev.filter(x => x.id !== deleteTarget.id));
      setDeleteTarget(null);
      setPwdDialog(null);
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur";
      setPwdDialog((d) => d ? { ...d, error: msg } : d);
    } finally { setPwdSubmitting(false); }
  };

  const submitGift = async () => {
    if (!giftTarget) return;
    setGiftSubmitting(true);
    try {
      const { data } = await api.post(`/admin/users/${giftTarget.id}/grant-subscription`, { duration: giftDuration });
      toast.success(`Abonnement de ${data.duration_label} offert à ${giftTarget.email}. Expire le ${new Date(data.subscription_expires).toLocaleDateString("fr-CA")}.`);
      setUsers(prev => prev.map(x => x.id === giftTarget.id ? { ...x, subscription_status: "active", subscription_expires: data.subscription_expires, subscription_granted_by_admin: true } : x));
      setGiftTarget(null);
      setGiftDuration("3m");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setGiftSubmitting(false); }
  };

  const submitEmailEdit = async () => {
    if (!emailTarget) return;
    const trimmed = (emailInput || "").trim().toLowerCase();
    if (!trimmed) { setEmailError("L'adresse ne peut pas être vide"); return; }
    if (trimmed === (emailTarget.email || "").toLowerCase()) { setEmailError("Aucun changement"); return; }
    setEmailError("");
    setEmailSubmitting(true);
    try {
      const { data } = await api.put(`/admin/users/${emailTarget.id}/email`, { email: trimmed });
      toast.success(`Courriel mis à jour : ${data.email}`);
      setUsers(prev => prev.map(x => x.id === emailTarget.id ? { ...x, email: data.email } : x));
      setEmailTarget(null);
      setEmailInput("");
    } catch (err) {
      setEmailError(err.response?.data?.detail || "Erreur");
    } finally { setEmailSubmitting(false); }
  };

  const submitRevoke = async () => {
    if (!revokeTarget) return;
    setRevokeSubmitting(true);
    try {
      await api.delete(`/admin/users/${revokeTarget.id}/subscription`);
      toast.success(`Abonnement de ${revokeTarget.email} révoqué`);
      setUsers(prev => prev.map(x => x.id === revokeTarget.id ? { ...x, subscription_status: "expired", subscription_expires: new Date().toISOString() } : x));
      setRevokeTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setRevokeSubmitting(false); }
  };

  const openHistory = async (u) => {
    setHistoryTarget(u);
    setHistoryRows([]);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/subscription-history`);
      setHistoryRows(data.history || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de chargement de l'historique");
    } finally { setHistoryLoading(false); }
  };

  const loadRefunds = async () => {
    setRefundsLoading(true);
    try {
      const { data } = await api.get("/admin/refunds");
      setRefundsData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de chargement des remboursements");
    } finally { setRefundsLoading(false); }
  };

  const loadReports = async (filterValue = reportsFilter) => {
    setReportsLoading(true);
    try {
      const { data } = await api.get("/admin/reports", { params: filterValue && filterValue !== "all" ? { status: filterValue } : {} });
      setReports(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de chargement des signalements");
    } finally { setReportsLoading(false); }
  };

  const setReportStatus = async (id, status) => {
    try {
      await api.put(`/admin/reports/${id}`, { status });
      toast.success("Statut mis à jour");
      loadReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center"><Shield className="h-5 w-5 text-emerald-700" /></div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-950" data-testid="admin-panel-title">Panneau admin</h1>
          <p className="text-sm text-zinc-500">Gestion des tarifs, des admins et des comptes</p>
        </div>
      </div>

      {/* Dashboard stats */}
      <AdminDashboardStats />

      {/* Pricing config */}
      <Card className="border border-zinc-200 rounded-2xl" data-testid="pricing-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2"><DollarSign className="h-5 w-5 text-emerald-600" /> Tarifs de la plateforme</CardTitle>
          <CardDescription>Ces valeurs s'appliquent immédiatement à toutes les transactions futures</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Prix d'abonnement (par an, en $)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={config.subscription_price}
                onChange={(e) => setConfig(c => ({ ...c, subscription_price: e.target.value }))}
                className="mt-1 rounded-xl"
                data-testid="subscription-price-input"
              />
            </div>
            <div>
              <Label>Frais transactionnels (ex: 0.03 = 3%)</Label>
              <Input
                type="number"
                step="0.001"
                min="0"
                max="0.5"
                value={config.platform_fee_rate}
                onChange={(e) => setConfig(c => ({ ...c, platform_fee_rate: e.target.value }))}
                className="mt-1 rounded-xl"
                data-testid="platform-fee-input"
              />
              <p className="text-xs text-zinc-400 mt-1">{(parseFloat(config.platform_fee_rate) * 100 || 0).toFixed(2)}% sur chaque réservation</p>
            </div>
            <div>
              <Label>Prix minimum par nuit ($)</Label>
              <Input
                type="number"
                step="0.50"
                min="0"
                max="1000"
                value={config.min_price_per_night}
                onChange={(e) => setConfig(c => ({ ...c, min_price_per_night: e.target.value }))}
                className="mt-1 rounded-xl"
                data-testid="min-price-per-night-input"
                placeholder="0 = désactivé"
              />
              <p className="text-xs text-zinc-400 mt-1">Les hôtes ne peuvent pas publier un terrain à un prix inférieur. Protège des frais Stripe fixes sur petites transactions.</p>
            </div>
          </div>
          <Button onClick={saveConfig} className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl" data-testid="save-pricing-btn">
            Enregistrer les tarifs
          </Button>
        </CardContent>
      </Card>

      {/* Users management (collapsible) */}
      <Card className="border border-zinc-200 rounded-2xl" data-testid="users-card">
        <button
          type="button"
          onClick={() => setUsersOpen(o => !o)}
          className="w-full text-left"
          data-testid="toggle-users-section"
          aria-expanded={usersOpen}
        >
          <CardHeader className="hover:bg-zinc-50/60 transition-colors rounded-t-2xl">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <CardTitle className="font-heading text-lg flex items-center gap-2"><Users className="h-5 w-5 text-emerald-600" /> Gestion des utilisateurs</CardTitle>
                <CardDescription>Donner/revoquer les droits admin, supprimer des comptes frauduleux</CardDescription>
              </div>
              {usersOpen ? <ChevronUp className="h-5 w-5 text-zinc-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-zinc-400 flex-shrink-0" />}
            </div>
          </CardHeader>
        </button>
        {usersOpen && (
        <CardContent>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") loadUsers(search); }}
              placeholder="Rechercher par email ou nom (Entree pour valider)"
              className="pl-10 rounded-xl"
              data-testid="user-search-input"
            />
          </div>

          {/* Filters + sort */}
          <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
            <span className="text-zinc-500 uppercase tracking-[0.15em] font-semibold mr-1">Filtre :</span>
            {[
              { v: "all", label: "Tous" },
              { v: "admin", label: "Admins" },
              { v: "user", label: "Membres" },
              { v: "suspended", label: "Suspendus" },
            ].map(opt => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setFilterRole(opt.v)}
                className={`px-3 py-1.5 rounded-full border transition ${filterRole === opt.v ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-zinc-700 border-zinc-200 hover:border-emerald-300"}`}
                data-testid={`filter-role-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
            <span className="mx-2 h-4 w-px bg-zinc-200" />
            <span className="text-zinc-500 uppercase tracking-[0.15em] font-semibold mr-1">Trier :</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-full border border-zinc-200 px-3 py-1.5 text-xs bg-white hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              data-testid="sort-users-select"
            >
              <option value="recent">Inscription récente</option>
              <option value="oldest">Inscription la plus ancienne</option>
              <option value="name">Ordre alphabetique</option>
              <option value="spaces">Nombre de terrains</option>
              <option value="bookings">Nombre de réservations</option>
            </select>
            <span className="ml-auto text-zinc-400">{filteredSortedUsers.length} resultat{filteredSortedUsers.length > 1 ? "s" : ""}</span>
          </div>

          {/* Bulk action toolbar — visible only when at least one row is selected */}
          {bulkSelected.size > 0 && (
            <div
              className="flex items-center justify-between gap-3 mb-4 px-3 py-2 rounded-xl bg-red-50 border border-red-200"
              data-testid="bulk-action-bar"
            >
              <p className="text-sm text-red-700 font-medium">
                <strong>{bulkSelected.size}</strong> compte{bulkSelected.size > 1 ? "s" : ""} sélectionné{bulkSelected.size > 1 ? "s" : ""}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-xl h-8 text-xs"
                  onClick={() => setBulkSelected(new Set())}
                  data-testid="bulk-clear-btn"
                >
                  Désélectionner
                </Button>
                <Button
                  size="sm"
                  className="rounded-xl h-8 text-xs bg-red-600 hover:bg-red-700 text-white gap-1"
                  onClick={() => setBulkDialog({ password: "", error: "" })}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Supprimer la sélection
                </Button>
              </div>
            </div>
          )}
          {loadingUsers ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>
          ) : filteredSortedUsers.length === 0 ? (
            <div className="text-center py-10 text-sm text-zinc-400">Aucun utilisateur trouve</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-zinc-500 uppercase tracking-wider border-b border-zinc-200">
                    <th className="text-left py-2 font-medium w-8">
                      <input
                        type="checkbox"
                        checked={isAllDeletableSelected}
                        onChange={toggleBulkAll}
                        className="h-4 w-4 cursor-pointer accent-red-600"
                        aria-label="Tout sélectionner"
                        data-testid="bulk-select-all"
                      />
                    </th>
                    <th className="text-left py-2 font-medium">Utilisateur</th>
                    <th className="text-left py-2 font-medium hidden sm:table-cell">Role</th>
                    <th className="text-left py-2 font-medium hidden md:table-cell">Activité</th>
                    <th className="text-right py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSortedUsers.map((u, i) => (
                    <tr key={u.id} className={`border-b border-zinc-100 hover:bg-zinc-50/50 ${bulkSelected.has(u.id) ? "bg-red-50/40" : ""}`} data-testid={`user-row-${i}`}>
                      <td className="py-3 align-top">
                        {u.id !== user.id && !u.is_super_admin && (
                          <input
                            type="checkbox"
                            checked={bulkSelected.has(u.id)}
                            onChange={() => toggleBulk(u.id)}
                            className="h-4 w-4 cursor-pointer accent-red-600 mt-1"
                            aria-label={`Sélectionner ${u.email}`}
                            data-testid={`bulk-select-${i}`}
                          />
                        )}
                      </td>
                      <td className="py-3">
                        <div>
                          <p className="font-semibold text-zinc-900 text-sm">{u.name || "Sans nom"}</p>
                          <p className="text-xs text-zinc-500">{u.email}</p>
                          {u.created_at && <p className="text-[10px] text-zinc-400 mt-0.5">Inscrit le {(() => { try { return format(new Date(u.created_at), "d MMM yyyy", { locale: fr }); } catch { return ""; } })()}</p>}
                        </div>
                      </td>
                      <td className="py-3 hidden sm:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {u.is_super_admin ? (
                            <Badge className="bg-amber-100 text-amber-700 border-0 gap-1"><Crown className="h-3 w-3" /> Super admin</Badge>
                          ) : u.role === "admin" ? (
                            <Badge className="bg-emerald-100 text-emerald-700 border-0 gap-1"><Shield className="h-3 w-3" /> Admin</Badge>
                          ) : (
                            <Badge className="bg-zinc-100 text-zinc-600 border-0">Membre</Badge>
                          )}
                          {u.suspended && (
                            <Badge className="bg-red-100 text-red-700 border-0 gap-1"><Ban className="h-3 w-3" /> Suspendu</Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 hidden md:table-cell">
                        <div className="text-xs text-zinc-500 space-y-0.5">
                          <p>{u.spaces_count} terrain{u.spaces_count > 1 ? "s" : ""}</p>
                          <p>{u.bookings_count} réservation{u.bookings_count > 1 ? "s" : ""}</p>
                          {u.rating_count > 0 && <p className="flex items-center gap-1"><Star className="h-2.5 w-2.5 fill-amber-400 text-amber-400" /> {u.avg_rating}/5 ({u.rating_count})</p>}
                        </div>
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex gap-2 justify-end">
                          {u.id !== user.id && !u.is_super_admin && (
                            <>
                              {user.is_super_admin && (
                                <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => toggleAdmin(u)} data-testid={`toggle-admin-${i}`} title={u.role === "admin" ? "Retirer admin" : "Promouvoir admin"}>
                                  {u.role === "admin" ? <UserMinus className="h-3.5 w-3.5 text-amber-600" /> : <UserCheck className="h-3.5 w-3.5 text-emerald-600" />}
                                </Button>
                              )}
                              {u.role !== "admin" && (
                                <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => { setEmailTarget(u); setEmailInput(u.email || ""); setEmailError(""); }} data-testid={`edit-email-${i}`} title="Modifier le courriel">
                                  <Mail className="h-3.5 w-3.5 text-blue-600" />
                                </Button>
                              )}
                              {u.role !== "admin" && (
                                <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => { setGiftTarget(u); setGiftDuration("3m"); }} data-testid={`gift-subscription-${i}`} title="Offrir un abonnement gratuit">
                                  <Gift className="h-3.5 w-3.5 text-emerald-600" />
                                </Button>
                              )}
                              {u.role !== "admin" && (
                                <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => openHistory(u)} data-testid={`subscription-history-${i}`} title="Historique d'abonnement">
                                  <History className="h-3.5 w-3.5 text-zinc-600" />
                                </Button>
                              )}
                              {u.role !== "admin" && u.subscription_status === "active" && (
                                <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setRevokeTarget(u)} data-testid={`revoke-subscription-${i}`} title="Retirer l'abonnement de cet utilisateur">
                                  <CalendarX className="h-3.5 w-3.5 text-orange-600" />
                                </Button>
                              )}
                              {u.role !== "admin" && (
                                <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => toggleSuspend(u)} data-testid={`toggle-suspend-${i}`} title={u.suspended ? "Reactiver le compte" : "Suspendre le compte"}>
                                  {u.suspended ? <Play className="h-3.5 w-3.5 text-emerald-600" /> : <Ban className="h-3.5 w-3.5 text-red-500" />}
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => setDeleteTarget(u)} data-testid={`delete-user-${i}`} title="Supprimer ce compte">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        )}
      </Card>

      {/* Refunds & litigation (collapsible) */}
      <Card className="border border-zinc-200 rounded-2xl" data-testid="refunds-card">
        <button
          type="button"
          onClick={() => { const next = !refundsOpen; setRefundsOpen(next); if (next && refundsData.rows.length === 0) loadRefunds(); }}
          className="w-full text-left"
          data-testid="toggle-refunds-section"
          aria-expanded={refundsOpen}
        >
          <CardHeader className="hover:bg-zinc-50/60 transition-colors rounded-t-2xl">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <RotateCcw className="h-5 w-5 text-orange-600" /> Litiges & remboursements
                </CardTitle>
                <CardDescription>Toutes les annulations + remboursements Stripe pour la comptabilité et le suivi des litiges</CardDescription>
              </div>
              {refundsOpen ? <ChevronUp className="h-5 w-5 text-zinc-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-zinc-400 flex-shrink-0" />}
            </div>
          </CardHeader>
        </button>
        {refundsOpen && (
          <CardContent>
            {refundsLoading ? (
              <div className="text-center py-10 text-sm text-zinc-500">
                <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Chargement...
              </div>
            ) : refundsData.rows.length === 0 ? (
              <div className="text-center py-10 text-sm text-zinc-400">
                <RotateCcw className="h-8 w-8 text-zinc-200 mx-auto mb-2" />
                Aucune annulation/remboursement enregistré.
              </div>
            ) : (
              <>
                {/* Summary tiles */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="refunds-summary">
                  <div className="p-3 rounded-xl bg-zinc-50 border border-zinc-200">
                    <p className="text-[11px] uppercase tracking-wide text-zinc-500">Nombre d'annulations</p>
                    <p className="text-2xl font-bold text-zinc-950">{refundsData.count}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-red-50 border border-red-200">
                    <p className="text-[11px] uppercase tracking-wide text-red-700">Total remboursé aux campeurs</p>
                    <p className="text-2xl font-bold text-red-900">{refundsData.total_refunded.toFixed(2)} $</p>
                  </div>
                  <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                    <p className="text-[11px] uppercase tracking-wide text-emerald-700">Frais d'annulation conservés</p>
                    <p className="text-2xl font-bold text-emerald-900">{refundsData.total_fees_kept.toFixed(2)} $</p>
                  </div>
                </div>

                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1" data-testid="refunds-list">
                  {refundsData.rows.map((r) => {
                    const tierStyle = {
                      free:     { bg: "bg-emerald-50",  border: "border-emerald-200", label: "100 % remboursé (>7j)" },
                      partial:  { bg: "bg-amber-50",    border: "border-amber-200",   label: "70 % remboursé (30 % frais)" },
                      too_late: { bg: "bg-red-50",      border: "border-red-200",     label: "Trop tard (<48h) — aucun remboursement" },
                      unpaid:   { bg: "bg-zinc-50",     border: "border-zinc-200",    label: "Annulée avant paiement" },
                    }[r.refund_tier] || { bg: "bg-zinc-50", border: "border-zinc-200", label: r.refund_tier || "—" };
                    const stripeUrl = r.stripe_refund_id ? `https://dashboard.stripe.com/refunds/${r.stripe_refund_id}` : (r.stripe_charge_id ? `https://dashboard.stripe.com/payments/${r.stripe_charge_id}` : null);
                    return (
                      <div key={r.booking_id} className={`p-3 rounded-xl border ${tierStyle.bg} ${tierStyle.border}`} data-testid={`refund-row-${r.booking_id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-zinc-900 text-sm truncate">{r.space_title || "(terrain supprimé)"}</p>
                              <Badge variant="outline" className="text-[10px] rounded-full bg-white">{tierStyle.label}</Badge>
                            </div>
                            <p className="text-[11px] text-zinc-500 mt-0.5">
                              {r.check_in?.slice(0, 10)} → {r.check_out?.slice(0, 10)} · annulée le {r.cancelled_at ? new Date(r.cancelled_at).toLocaleDateString("fr-CA") : "?"}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-xs text-zinc-500">Payé : <strong className="text-zinc-900">{r.total_price.toFixed(2)} $</strong></p>
                            {r.refund_amount > 0 && <p className="text-xs text-zinc-500">Remboursé : <strong className="text-red-700">{r.refund_amount.toFixed(2)} $</strong></p>}
                            {r.refund_fee_amount > 0 && <p className="text-xs text-zinc-500">Frais conservés : <strong className="text-emerald-700">{r.refund_fee_amount.toFixed(2)} $</strong></p>}
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-600">
                          <span>👤 Campeur : <strong>{r.camper?.name || "?"}</strong> <code className="text-[10px] bg-white px-1 rounded">{r.camper?.email || "?"}</code></span>
                          <span>🏕️ Hôte : <strong>{r.host?.name || "?"}</strong> <code className="text-[10px] bg-white px-1 rounded">{r.host?.email || "?"}</code></span>
                        </div>
                        {stripeUrl && (
                          <div className="mt-2">
                            <a href={stripeUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#635bff] hover:underline" data-testid={`stripe-link-${r.booking_id}`}>
                              Voir sur Stripe →
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        )}
      </Card>


      {/* Reports / signalements (collapsible) */}
      <Card className="border border-zinc-200 rounded-2xl" data-testid="reports-card">
        <button
          type="button"
          onClick={() => { const next = !reportsOpen; setReportsOpen(next); if (next && reports.length === 0) loadReports(); }}
          className="w-full text-left"
          data-testid="toggle-reports-section"
          aria-expanded={reportsOpen}
        >
          <CardHeader className="hover:bg-zinc-50/60 transition-colors rounded-t-2xl">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Flag className="h-5 w-5 text-red-600" /> Signalements
                </CardTitle>
                <CardDescription>Abus signalés par les utilisateurs (terrains dangereux, comportements problématiques, etc.)</CardDescription>
              </div>
              {reportsOpen ? <ChevronUp className="h-5 w-5 text-zinc-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-zinc-400 flex-shrink-0" />}
            </div>
          </CardHeader>
        </button>
        {reportsOpen && (
          <CardContent>
            <div className="flex gap-2 flex-wrap mb-4">
              {[
                { key: "pending", label: "En attente" },
                { key: "reviewed", label: "Examinés" },
                { key: "resolved", label: "Résolus" },
                { key: "dismissed", label: "Rejetés" },
                { key: "all", label: "Tous" },
              ].map(f => (
                <Button
                  key={f.key}
                  size="sm"
                  variant={reportsFilter === f.key ? "default" : "outline"}
                  className={`rounded-xl ${reportsFilter === f.key ? "bg-emerald-700 text-white hover:bg-emerald-800" : ""}`}
                  onClick={() => { setReportsFilter(f.key); loadReports(f.key); }}
                  data-testid={`reports-filter-${f.key}`}
                >
                  {f.label}
                </Button>
              ))}
            </div>
            {reportsLoading ? (
              <div className="text-center py-10 text-sm text-zinc-500">
                <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Chargement...
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-10 text-sm text-zinc-400" data-testid="no-reports">
                <Flag className="h-8 w-8 text-zinc-200 mx-auto mb-2" />
                Aucun signalement {reportsFilter !== "all" ? `(${reportsFilter})` : ""}
              </div>
            ) : (
              <div className="space-y-3">
                {reports.map((r) => (
                  <div key={r.id} className="rounded-xl border border-zinc-200 p-4 bg-white" data-testid={`report-row-${r.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge className="bg-red-100 text-red-700 border-red-200">{r.reason_label}</Badge>
                          <Badge variant="outline" className="capitalize">{r.target_type}</Badge>
                          <Badge variant="outline" className={
                            r.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200"
                              : r.status === "reviewed" ? "bg-blue-50 text-blue-700 border-blue-200"
                              : r.status === "resolved" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-zinc-100 text-zinc-500"
                          }>{r.status}</Badge>
                        </div>
                        <p className="text-xs text-zinc-500">Signalé par <strong className="text-zinc-700">{r.reporter_name}</strong> ({r.reporter_email}) — {(() => { try { return new Date(r.created_at).toLocaleString("fr-CA"); } catch { return r.created_at; } })()}</p>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        {r.target_type === "space" && r.target_id && (
                          <Button size="sm" variant="outline" className="rounded-xl h-7 text-xs gap-1" onClick={() => window.open(`/spaces/${r.target_id}`, "_blank")} data-testid={`open-target-${r.id}`}>
                            <ExternalLink className="h-3 w-3" /> Voir
                          </Button>
                        )}
                        {r.target_type === "user" && r.target_id && (
                          <Button size="sm" variant="outline" className="rounded-xl h-7 text-xs gap-1" onClick={() => window.open(`/users/${r.target_id}`, "_blank")} data-testid={`open-target-${r.id}`}>
                            <ExternalLink className="h-3 w-3" /> Voir
                          </Button>
                        )}
                      </div>
                    </div>
                    {r.snapshot && (
                      <p className="text-xs text-zinc-600 mb-1">
                        Cible : <strong>{r.snapshot.title || r.snapshot.name || r.snapshot.space_title || "?"}</strong>
                        {r.snapshot.address ? ` — ${r.snapshot.address}` : ""}
                        {r.snapshot.email ? ` — ${r.snapshot.email}` : ""}
                      </p>
                    )}
                    {r.description && (
                      <div className="text-sm text-zinc-700 italic bg-zinc-50 rounded-lg p-2 my-2">"{r.description}"</div>
                    )}
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {r.status !== "reviewed" && <Button size="sm" variant="outline" className="rounded-xl h-7 text-xs" onClick={() => setReportStatus(r.id, "reviewed")} data-testid={`mark-reviewed-${r.id}`}>Marquer examiné</Button>}
                      {r.status !== "resolved" && <Button size="sm" className="rounded-xl h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setReportStatus(r.id, "resolved")} data-testid={`mark-resolved-${r.id}`}>Résoudre</Button>}
                      {r.status !== "dismissed" && <Button size="sm" variant="outline" className="rounded-xl h-7 text-xs text-red-600 border-red-200" onClick={() => setReportStatus(r.id, "dismissed")} data-testid={`mark-dismissed-${r.id}`}>Rejeter</Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>


      {/* Link to site settings */}
      <Card className="border border-zinc-200 rounded-2xl">
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <p className="font-semibold text-zinc-900 flex items-center gap-2"><Settings2 className="h-4 w-4 text-emerald-600" /> Personnalisation du site</p>
            <p className="text-xs text-zinc-500">Logo, page d'accueil, textes, temoignages...</p>
          </div>
          <Button variant="outline" className="rounded-xl" onClick={() => navigate("/settings")} data-testid="go-site-settings">Ouvrir</Button>
        </CardContent>
      </Card>

      {/* Link to identity vérifications */}
      <Card className="border border-zinc-200 rounded-2xl">
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <p className="font-semibold text-zinc-900 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Verifications d'identité</p>
            <p className="text-xs text-zinc-500">Approuver ou refuser les documents d'identité des hôtes</p>
          </div>
          <Button variant="outline" className="rounded-xl" onClick={() => navigate("/admin/verifications")} data-testid="go-verifications">Ouvrir</Button>
        </CardContent>
      </Card>

      {/* Waitlist viewer */}
      <WaitlistCard />

      {/* Promote confirmation dialog (password) */}
      <Dialog open={!!promoteTarget} onOpenChange={(v) => { if (!v) { setPromoteTarget(null); setPromotePassword(""); } }}>
        <DialogContent className="rounded-2xl" data-testid="promote-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Shield className="h-5 w-5 text-emerald-600" /> Confirmer la promotion admin</DialogTitle>
            <DialogDescription>
              Cet utilisateur obtiendra tous les droits administrateur (gestion des tarifs, des comptes, etc.). Entrez votre mot de passe pour confirmer.
            </DialogDescription>
          </DialogHeader>
          {promoteTarget && (
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-sm">
              <p className="font-semibold text-emerald-900">{promoteTarget.name}</p>
              <p className="text-xs text-emerald-700">{promoteTarget.email}</p>
            </div>
          )}
          <div>
            <Label>Votre mot de passe admin</Label>
            <Input
              type="password"
              value={promotePassword}
              onChange={(e) => setPromotePassword(e.target.value)}
              placeholder="Mot de passe"
              className="mt-1 rounded-xl"
              onKeyDown={(e) => { if (e.key === "Enter") confirmPromote(); }}
              data-testid="promote-password-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => { setPromoteTarget(null); setPromotePassword(""); }} data-testid="promote-cancel-btn">Annuler</Button>
            <Button className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl" onClick={confirmPromote} disabled={promoting || !promotePassword} data-testid="promote-confirm-btn">
              {promoting ? "Promotion..." : "Confirmer la promotion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <DialogContent className="rounded-2xl" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" /> Supprimer ce compte ?</DialogTitle>
            <DialogDescription>
              Action irreversible. Seront supprimes : le compte, les terrains publies, les réservations (envoyees et reçues), les messages, les avis, les notifications.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-sm">
              <p className="font-semibold text-red-900">{deleteTarget.name}</p>
              <p className="text-xs text-red-700">{deleteTarget.email}</p>
              <p className="text-[11px] text-red-600 mt-1">{deleteTarget.spaces_count} terrain(s), {deleteTarget.bookings_count} réservation(s)</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setDeleteTarget(null)}>Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700 rounded-xl" onClick={confirmDelete} data-testid="confirm-delete-user-btn">
              Continuer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Edit email dialog */}
      <Dialog open={!!emailTarget} onOpenChange={(v) => { if (!v) { setEmailTarget(null); setEmailInput(""); setEmailError(""); } }}>
        <DialogContent className="rounded-2xl" data-testid="edit-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Mail className="h-5 w-5" /> Modifier l'adresse courriel
            </DialogTitle>
            <DialogDescription>
              Pour corriger une faute de frappe (ex. <code>homail.com</code> → <code>hotmail.com</code>).
              L'utilisateur recevra un courriel sur la nouvelle adresse et devra l'utiliser pour se connecter.
            </DialogDescription>
          </DialogHeader>
          {emailTarget && (
            <div className="space-y-4">
              <div className="p-3 rounded-xl bg-blue-50 border border-blue-100 text-sm">
                <p className="font-semibold text-blue-900">{emailTarget.name || "(sans nom)"}</p>
                <p className="text-xs text-blue-700">Adresse actuelle : <code>{emailTarget.email}</code></p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Nouvelle adresse</Label>
                <Input
                  value={emailInput}
                  onChange={(e) => { setEmailInput(e.target.value); setEmailError(""); }}
                  type="email"
                  autoComplete="off"
                  placeholder="utilisateur@exemple.com"
                  className="rounded-xl"
                  data-testid="edit-email-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && !emailSubmitting) submitEmailEdit(); }}
                />
                {emailError && <p className="text-xs text-red-600" data-testid="edit-email-error">{emailError}</p>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setEmailTarget(null)} disabled={emailSubmitting}>Annuler</Button>
            <Button
              className="bg-blue-600 text-white hover:bg-blue-700 rounded-xl"
              onClick={submitEmailEdit}
              disabled={emailSubmitting}
              data-testid="confirm-email-edit-btn"
            >
              {emailSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              Mettre à jour
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gift subscription dialog */}
      <Dialog open={!!giftTarget} onOpenChange={(v) => { if (!v) { setGiftTarget(null); setGiftDuration("3m"); } }}>
        <DialogContent className="rounded-2xl" data-testid="gift-subscription-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Gift className="h-5 w-5" /> Offrir un abonnement gratuit
            </DialogTitle>
            <DialogDescription>
              Cet abonnement sera offert gracieusement par VroumZy et s'ajoutera à l'abonnement actuel de l'utilisateur.
              L'utilisateur recevra une notification + un courriel.
            </DialogDescription>
          </DialogHeader>
          {giftTarget && (
            <div className="space-y-4">
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-sm">
                <p className="font-semibold text-emerald-900">{giftTarget.name || giftTarget.email}</p>
                <p className="text-xs text-emerald-700">{giftTarget.email}</p>
                {giftTarget.subscription_expires && giftTarget.subscription_status === "active" && (
                  <p className="text-[11px] text-emerald-600 mt-1">
                    Abonnement actuel expire le : {new Date(giftTarget.subscription_expires).toLocaleDateString("fr-CA")}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Durée offerte</Label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="gift-duration-options">
                  {[
                    { v: "1m", l: "1 mois" },
                    { v: "3m", l: "3 mois" },
                    { v: "6m", l: "6 mois" },
                    { v: "1y", l: "1 an" },
                    { v: "unlimited", l: "Illimité" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setGiftDuration(opt.v)}
                      className={`px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${
                        giftDuration === opt.v
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-zinc-700 border-zinc-200 hover:border-emerald-300 hover:bg-emerald-50"
                      }`}
                      data-testid={`gift-duration-${opt.v}`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setGiftTarget(null)} disabled={giftSubmitting}>Annuler</Button>
            <Button
              className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl"
              onClick={submitGift}
              disabled={giftSubmitting}
              data-testid="confirm-gift-btn"
            >
              {giftSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Gift className="h-4 w-4 mr-2" />}
              Offrir l'abonnement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription history dialog */}
      <Dialog open={!!historyTarget} onOpenChange={(v) => { if (!v) { setHistoryTarget(null); setHistoryRows([]); } }}>
        <DialogContent className="rounded-2xl max-w-2xl" data-testid="subscription-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-zinc-700" /> Historique d'abonnement
            </DialogTitle>
            <DialogDescription>
              Audit trail complet des changements d'abonnement pour cet utilisateur.
            </DialogDescription>
          </DialogHeader>
          {historyTarget && (
            <div className="p-3 rounded-xl bg-zinc-100 text-sm">
              <p className="font-semibold text-zinc-900">{historyTarget.name || historyTarget.email}</p>
              <p className="text-xs text-zinc-600">{historyTarget.email}</p>
            </div>
          )}
          <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-2" data-testid="history-list">
            {historyLoading ? (
              <div className="text-center py-6 text-sm text-zinc-500">
                <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Chargement…
              </div>
            ) : historyRows.length === 0 ? (
              <p className="text-center py-6 text-sm text-zinc-400">Aucun changement enregistré pour cet utilisateur.</p>
            ) : (
              historyRows.map((row) => {
                const styles = {
                  granted:        { bg: "bg-emerald-50",  border: "border-emerald-200", dot: "bg-emerald-500", icon: "🎁", label: "Abonnement offert" },
                  revoked:        { bg: "bg-orange-50",   border: "border-orange-200",  dot: "bg-orange-500",  icon: "📅✗", label: "Abonnement révoqué" },
                  purchased:      { bg: "bg-blue-50",     border: "border-blue-200",    dot: "bg-blue-500",    icon: "💳", label: "Achat via Stripe" },
                  renewed:        { bg: "bg-blue-50",     border: "border-blue-200",    dot: "bg-blue-500",    icon: "🔄", label: "Renouvellement automatique" },
                  free_activated: { bg: "bg-emerald-50",  border: "border-emerald-200", dot: "bg-emerald-500", icon: "🎉", label: "Activation gratuite (mode prix=0)" },
                  expired:        { bg: "bg-red-50",      border: "border-red-200",     dot: "bg-red-500",     icon: "⌛", label: "Expiré (fin de période de grâce)" },
                }[row.event] || { bg: "bg-zinc-50", border: "border-zinc-200", dot: "bg-zinc-400", icon: "•", label: row.event };
                const d = row.details || {};
                return (
                  <div key={row.id} className={`p-3 rounded-xl border ${styles.bg} ${styles.border}`} data-testid={`history-row-${row.event}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2">
                        <span className="text-lg leading-none mt-0.5">{styles.icon}</span>
                        <div>
                          <p className="font-medium text-zinc-900 text-sm">{styles.label}</p>
                          <p className="text-[11px] text-zinc-500 mt-0.5">{new Date(row.created_at).toLocaleString("fr-CA", { dateStyle: "long", timeStyle: "short" })}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 pl-7 text-xs text-zinc-700 space-y-0.5">
                      {d.duration_label && <p>• Durée : <strong>{d.duration_label}</strong></p>}
                      {d.amount != null && <p>• Montant : <strong>{Number(d.amount).toFixed(2)} $</strong></p>}
                      {d.expires_at && <p>• Nouvelle expiration : <strong>{new Date(d.expires_at).toLocaleDateString("fr-CA")}</strong></p>}
                      {d.previous_expires && <p>• Expiration précédente : {new Date(d.previous_expires).toLocaleDateString("fr-CA")}</p>}
                      {d.by_email && <p>• Par : <code className="bg-white px-1 rounded">{d.by_email}</code></p>}
                      {d.reason && <p>• Raison : {d.reason}</p>}
                      {d.stripe_session_id && <p className="text-[10px] text-zinc-500 truncate">Stripe session : <code>{d.stripe_session_id}</code></p>}
                      {d.stripe_payment_intent_id && <p className="text-[10px] text-zinc-500 truncate">Stripe PI : <code>{d.stripe_payment_intent_id}</code></p>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setHistoryTarget(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Revoke subscription confirm dialog */}
      <Dialog open={!!revokeTarget} onOpenChange={(v) => { if (!v) setRevokeTarget(null); }}>
        <DialogContent className="rounded-2xl" data-testid="revoke-subscription-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <CalendarX className="h-5 w-5" /> Retirer l'abonnement
            </DialogTitle>
            <DialogDescription>
              L'abonnement sera révoqué immédiatement. Les terrains publiés par cet utilisateur ne seront plus visibles
              publiquement jusqu'à ce qu'il se réabonne. Une notification + un courriel seront envoyés à l'utilisateur.
            </DialogDescription>
          </DialogHeader>
          {revokeTarget && (
            <div className="p-3 rounded-xl bg-orange-50 border border-orange-100 text-sm">
              <p className="font-semibold text-orange-900">{revokeTarget.name || revokeTarget.email}</p>
              <p className="text-xs text-orange-700">{revokeTarget.email}</p>
              {revokeTarget.subscription_expires && (
                <p className="text-[11px] text-orange-600 mt-1">
                  Expiration actuelle : {new Date(revokeTarget.subscription_expires).toLocaleDateString("fr-CA")}
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setRevokeTarget(null)} disabled={revokeSubmitting}>Annuler</Button>
            <Button
              className="bg-orange-600 text-white hover:bg-orange-700 rounded-xl"
              onClick={submitRevoke}
              disabled={revokeSubmitting}
              data-testid="confirm-revoke-btn"
            >
              {revokeSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarX className="h-4 w-4 mr-2" />}
              Retirer l'abonnement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Generic admin password dialog (config save / delete) */}
      <AdminPasswordDialog
        open={!!pwdDialog}
        onOpenChange={(v) => { if (!v) setPwdDialog(null); }}
        loading={pwdSubmitting}
        error={pwdDialog?.error || ""}
        title={pwdDialog?.kind === "delete" ? "Confirmer la suppression" : "Confirmer la modification"}
        description={
          pwdDialog?.kind === "delete"
            ? `Cette action supprimera definitivement le compte ${pwdDialog?.target?.email || ""} et toutes ses donnees. Saisissez votre mot de passe administrateur pour confirmer.`
            : "Saisissez votre mot de passe administrateur pour appliquer les nouveaux tarifs."
        }
        confirmLabel={pwdDialog?.kind === "delete" ? "Supprimer definitivement" : "Confirmer"}
        onConfirm={(pwd) => {
          if (pwdDialog?.kind === "delete") submitDeleteWithPassword(pwd);
          else if (pwdDialog?.kind === "config") submitConfigWithPassword(pwd);
        }}
      />

      {/* Bulk-delete confirmation dialog (separate dialog so we can show a count) */}
      <AdminPasswordDialog
        open={!!bulkDialog}
        onOpenChange={(v) => { if (!v) setBulkDialog(null); }}
        loading={bulkSubmitting}
        error={bulkDialog?.error || ""}
        title={`Supprimer ${bulkSelected.size} compte${bulkSelected.size > 1 ? "s" : ""} ?`}
        description={`Cette action supprimera définitivement ${bulkSelected.size} compte${bulkSelected.size > 1 ? "s" : ""} et l'intégralité de leurs données (terrains, réservations, messages, avis, favoris, signalements). Saisissez votre mot de passe administrateur pour confirmer.`}
        confirmLabel="Supprimer tout"
        onConfirm={submitBulkDelete}
      />
    </div>
  );
}


function WaitlistCard() {
  const [data, setData] = useState({ count: 0, items: [] });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/waitlist");
      setData(d || { count: 0, items: [] });
    } catch {
      // ignore
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const exportCsv = () => {
    const rows = [["email", "name", "created_at"], ...data.items.map(i => [i.email, i.name || "", i.created_at])];
    const csv = rows.map(r => r.map(c => `"${(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const remove = async (email) => {
    if (!window.confirm(`Retirer ${email} de la liste d'attente ?`)) return;
    try {
      await api.delete(`/admin/waitlist/${encodeURIComponent(email)}`);
      toast.success("Retiré");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <Card className="border border-zinc-200 rounded-2xl" data-testid="waitlist-card">
      <CardContent className="p-4">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between"
          data-testid="waitlist-toggle"
        >
          <div className="text-left">
            <p className="font-semibold text-zinc-900 flex items-center gap-2">
              <Users className="h-4 w-4 text-amber-600" /> Liste d'attente (pré-lancement)
            </p>
            <p className="text-xs text-zinc-500">{data.count} inscrit{data.count > 1 ? "s" : ""}</p>
          </div>
          {open ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
        </button>

        {open && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2">
              <Button onClick={load} disabled={loading} size="sm" variant="outline" className="rounded-xl">
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Actualiser"}
              </Button>
              {data.items.length > 0 && (
                <Button onClick={exportCsv} size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl" data-testid="export-waitlist-btn">
                  Exporter CSV
                </Button>
              )}
            </div>
            {data.items.length === 0 ? (
              <p className="text-sm text-zinc-500 italic">Aucune inscription pour le moment.</p>
            ) : (
              <div className="border border-zinc-200 rounded-xl overflow-hidden max-h-96 overflow-y-auto" data-testid="waitlist-table">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left">Courriel</th>
                      <th className="px-3 py-2 text-left">Prénom</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <tr key={item.email} className="border-t border-zinc-100">
                        <td className="px-3 py-2 font-medium text-zinc-900">{item.email}</td>
                        <td className="px-3 py-2 text-zinc-600">{item.name || "-"}</td>
                        <td className="px-3 py-2 text-zinc-500 text-xs">{item.created_at ? format(new Date(item.created_at), "d MMM yyyy HH:mm", { locale: fr }) : "-"}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => remove(item.email)} className="text-red-600 hover:text-red-700" title="Retirer" data-testid={`remove-waitlist-${item.email}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
